import { useEffect, useState } from "react";
import { adminApi } from "../lib/api";
import { toast } from "sonner";
import { Printer, Package, Truck, CheckCheck } from "lucide-react";

const FULFILLMENT_OPTIONS = [
  { value: "pending", label: "Pending", icon: Package, color: "text-yellow-400 border-yellow-400" },
  { value: "shipped", label: "Shipped", icon: Truck, color: "text-blue-400 border-blue-400" },
  { value: "delivered", label: "Delivered", icon: CheckCheck, color: "text-green-400 border-green-400" },
];

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/orders");
      setOrders(data);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (order, status) => {
    try {
      await adminApi().put(`/orders/${order.id}/fulfillment?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const printOrder = (order) => {
    setSelected(order);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const paidTotal = orders
    .reduce((s, o) => s + (o.total || 0), 0)
    .toFixed(2);

  return (
    <div data-testid="orders-panel">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-green-400 mb-2">
            Sales
          </p>
          <h2 className="font-display text-4xl uppercase tracking-tighter">
            Orders
          </h2>
          <p className="text-zinc-500 text-sm mt-2 uppercase tracking-widest">
            {orders.length} {orders.length === 1 ? "order" : "orders"} · ${paidTotal} CAD revenue
          </p>
        </div>
        <button
          onClick={load}
          data-testid="orders-refresh"
          className="text-xs uppercase tracking-widest border border-zinc-800 hover:border-green-400 hover:text-green-400 px-4 py-2 transition-colors"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <p className="text-zinc-500 text-center py-10">Loading…</p>
      )}

      {!loading && orders.length === 0 && (
        <div className="border border-zinc-900 p-16 text-center text-zinc-500 uppercase tracking-widest text-sm">
          No orders yet. First sale incoming 🟢
        </div>
      )}

      <div className="space-y-4 print:hidden">
        {orders.map((o) => (
          <div
            key={o.id}
            data-testid={`order-${o.id}`}
            className="border border-zinc-900 bg-zinc-950 p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Order #{o.session_id?.slice(-12)}
                </p>
                <h3 className="font-display text-xl uppercase tracking-tight mt-1">
                  {o.customer_name || "—"}
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                  {o.customer_email || "—"} · {o.customer_phone || "no phone"}
                </p>
                <p className="text-[10px] text-zinc-600 mt-1">
                  {new Date(o.created_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="font-display text-2xl text-green-400">
                  ${o.total?.toFixed(2)} {o.currency?.toUpperCase()}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  {o.items?.length || 0} items
                </p>
              </div>
            </div>

            {/* Shipping address */}
            {o.shipping_address && (
              <div className="border border-zinc-900 p-3 mb-4 text-sm text-zinc-300">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  Shipping to
                </p>
                <p className="font-bold">{o.shipping_address.name}</p>
                <p>{o.shipping_address.line1}</p>
                {o.shipping_address.line2 && <p>{o.shipping_address.line2}</p>}
                <p>
                  {o.shipping_address.city}, {o.shipping_address.state}{" "}
                  {o.shipping_address.postal_code}
                </p>
                <p>{o.shipping_address.country}</p>
              </div>
            )}

            {/* Items */}
            <div className="space-y-2 mb-4">
              {(o.items || []).map((i, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 text-sm border-b border-zinc-900 pb-2 last:border-0"
                >
                  {i.image_url && (
                    <img
                      src={i.image_url}
                      alt=""
                      className="w-12 h-14 object-cover bg-black shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display uppercase tracking-tight truncate">
                      {i.name}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Size {i.size} · Qty {i.quantity}
                    </p>
                  </div>
                  <p className="text-green-400 font-display">
                    ${i.line_total?.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-zinc-900">
              <div className="flex gap-2">
                {FULFILLMENT_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = o.fulfillment_status === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setStatus(o, opt.value)}
                      data-testid={`order-${o.id}-status-${opt.value}`}
                      className={`flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${
                        active
                          ? `${opt.color} bg-black`
                          : "border-zinc-800 text-zinc-500 hover:border-white hover:text-white"
                      }`}
                    >
                      <Icon size={12} /> {opt.label}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => printOrder(o)}
                data-testid={`order-${o.id}-print`}
                className="flex items-center gap-2 bg-white text-black text-[10px] uppercase tracking-widest font-bold px-4 py-2 hover:bg-zinc-200 transition-colors"
              >
                <Printer size={12} /> Print label
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Print view (hidden until print) */}
      {selected && (
        <div className="hidden print:block p-8 bg-white text-black">
          <h1 className="text-3xl font-bold mb-2">ALLSTAR</h1>
          <p className="text-xs mb-4">allstarclub.shop · Order #{selected.session_id?.slice(-12)}</p>
          <hr className="my-4 border-black" />

          <p className="text-xs uppercase mb-1">Ship To:</p>
          <div className="text-lg leading-snug">
            <p className="font-bold">{selected.shipping_address?.name}</p>
            <p>{selected.shipping_address?.line1}</p>
            {selected.shipping_address?.line2 && <p>{selected.shipping_address.line2}</p>}
            <p>
              {selected.shipping_address?.city}, {selected.shipping_address?.state}{" "}
              {selected.shipping_address?.postal_code}
            </p>
            <p>{selected.shipping_address?.country}</p>
            {selected.customer_phone && <p className="mt-2 text-sm">{selected.customer_phone}</p>}
            {selected.customer_email && <p className="text-sm">{selected.customer_email}</p>}
          </div>

          <hr className="my-4 border-black" />
          <p className="text-xs uppercase mb-2">Items:</p>
          {(selected.items || []).map((i, idx) => (
            <div key={idx} className="flex justify-between border-b border-black py-2">
              <div>
                <p className="font-bold">{i.name}</p>
                <p className="text-sm">Size {i.size} · Qty {i.quantity}</p>
              </div>
              <p>${i.line_total?.toFixed(2)}</p>
            </div>
          ))}

          <div className="mt-4 text-right">
            <p>Subtotal: ${selected.subtotal?.toFixed(2)}</p>
            <p>Shipping: ${selected.shipping?.toFixed(2)}</p>
            <p className="font-bold text-xl mt-2">
              Total: ${selected.total?.toFixed(2)} {selected.currency?.toUpperCase()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
